import { Text } from '@thanx/uikit/text'
import { useLazyDownloadCustomerImportQuery } from 'api/userLists'
import Badge from 'components/Badge'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import {
  Fields as UserProfile,
  MembershipTag,
  UserListValue,
} from 'models/UserProfile'
import moment from 'moment-timezone'
import React from 'react'
import { Link } from 'react-router-dom'
import { useAbility } from 'utilities/ability'
import { getFormattedDate } from 'utilities/date'
import { useUserTimeZone } from 'utilities/userTimeZone'
import Counters from '../Counters'
import PiiBanner from './PiiBanner'
import Section from './Section'

const t = buildTranslate('users.view.profile.overview')

export const USER_LISTS_TAG_KEY = 'User Lists'
export const IMPORT_FILES_TAG_KEY = 'Import Files'

function getTagValues(
  tags: UserProfile['custom_tags'],
  key: string
): (string | UserListValue)[] {
  const tag = tags.find(t => t.type === 'membershipTag' && t.key === key) as
    | MembershipTag
    | undefined
  return tag?.values ?? []
}

function getUserListValues(tags: UserProfile['custom_tags']): UserListValue[] {
  const values = getTagValues(tags, USER_LISTS_TAG_KEY)
  return values.filter(
    (v): v is UserListValue =>
      typeof v === 'object' && v !== null && 'id' in v && 'name' in v
  )
}

function getStringTagValues(
  tags: UserProfile['custom_tags'],
  key: string
): string[] {
  const values = getTagValues(tags, key)
  return values.filter((v): v is string => typeof v === 'string')
}

type PropsT = {
  profile: UserProfile
}

const Overview: React.FC<PropsT> = ({ profile }: PropsT) => {
  const userTimeZone = useUserTimeZone()
  const [triggerDownload] = useLazyDownloadCustomerImportQuery()
  const userListsEnabled = useFlag('self-serve-list-upload', false)
  const ability = useAbility()
  const canAccessUserLists =
    userListsEnabled && ability.can('access', 'UserLists')
  const birth_date = (() => {
    const noDateAvailable = '-'

    if (!!profile.birth_day && !!profile.birth_month) {
      const m = moment.tz(
        `2000-${profile.birth_month}-${profile.birth_day}`,
        'YYYY-MM-DD',
        userTimeZone
      )
      return m.isValid() ? getFormattedDate(m, 'anniversary') : noDateAvailable
    }

    // keep legacy formatting as a fallback precaution against backend rollbacks
    if (!!profile.birth_date) {
      const m = moment.tz(profile.birth_date, userTimeZone)
      return m.isValid() ? getFormattedDate(m, 'daily') : noDateAvailable
    }

    return noDateAvailable
  })()

  const userListValues = getUserListValues(profile.custom_tags)
  const importFiles = getStringTagValues(
    profile.custom_tags,
    IMPORT_FILES_TAG_KEY
  ).slice(0, 10)

  const handleDownload = async (urlPath: string, filename: string) => {
    const result = await triggerDownload(urlPath)
    if (result.data) {
      const link = document.createElement('a')
      link.href = result.data
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(result.data)
    }
  }

  return (
    <div>
      <Text.Header4 className="mb-m">{t('title')}</Text.Header4>
      <Counters profile={profile} />
      {!profile.show_pii && <PiiBanner />}

      <Section title={t('contact')} icon={'envelope-o'} className={'mt-l mb-m'}>
        <Text.BodyText4 className="mb-xs" tag={'p'} bold>
          {t('email')}
        </Text.BodyText4>
        <Text.BodyText3 color="grey70">{profile.email ?? '-'}</Text.BodyText3>
        <Text.BodyText4 className="mb-xs mt-m" tag={'p'} bold>
          {t('phone')}
        </Text.BodyText4>
        <Text.BodyText3 color="grey70">{profile.phone ?? '-'}</Text.BodyText3>
      </Section>

      <Section title={t('profile')} icon={'user-o'} className={'mb-m'}>
        <Text.BodyText4 className="mb-xs" tag={'p'} bold>
          {t('birthday')}
        </Text.BodyText4>
        <Text.BodyText3 color="grey70">{birth_date}</Text.BodyText3>
        {canAccessUserLists && userListValues.length > 0 && (
          <>
            <Text.BodyText4 className="mb-xs mt-m" tag={'p'} bold>
              {t('user_lists')}
            </Text.BodyText4>
            <div className="flex-row">
              {userListValues.map(item => (
                <Link
                  key={item.id}
                  to={`/user_lists/${item.id}`}
                  className="no-underline mr-xs"
                >
                  <Badge
                    className="grey-70 grey-20-bkg d-inline"
                    value={item.name}
                  />
                </Link>
              ))}
            </div>
          </>
        )}
      </Section>

      {canAccessUserLists && importFiles.length > 0 && (
        <Section title={t('import_files')} icon={'file-o'} className={'mb-m'}>
          {importFiles.map((file, index) => (
            <a
              key={`import-file-${index}`}
              href="#"
              onClick={async e => {
                e.preventDefault()
                const filename = file.split('/').pop() || 'import.csv'
                await handleDownload(file, filename)
              }}
              className={`d-block${
                index < importFiles.length - 1 ? ' mb-xs' : ''
              }`}
            >
              <Text.BodyText3 tag={'span'} color="belizeHole50">
                {file.split('/').pop() || file}
              </Text.BodyText3>
            </a>
          ))}
        </Section>
      )}
    </div>
  )
}

export default Overview
